import ProgressBar from "@ramonak/react-progress-bar";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import Popup from "reactjs-popup";

import emptyImage from "~/assets/images/emptyAvatar.svg";
import Loader from "~/components/shared/Loader.jsx";
import useFileUpload from "~/hooks/useFileUpload.js";
import { cn } from "~/utils/utils.jsx";

import { useGetUserInformation, usersKeys } from "../../api/get-user-information.api";
import { useUpdateProfilePicture } from "../../api/update-profile-picture.api";
import styles from "./profile-image-container.module.css";

type ProfileImageContainerProps = {
  src?: string | false;
  width?: string;
  height?: string;
  className?: string;
};

const ProfileImageContainer = ({ src, width, height, className }: ProfileImageContainerProps) => {
  const queryClient = useQueryClient();
  const [_fileUrl, setFileUrl] = useState("");
  const [file, setFile] = useState<File | File[]>([]);
  const [fileIsUploading, setFileIsUploading] = useState(false);

  const { data: user } = useGetUserInformation();
  const userId = user?.id;
  const { mutateAsync: updatePicture } = useUpdateProfilePicture();

  const { progress, uploading, error, uploadFile } = useFileUpload();

  const handleUpload = async ({ file: nextFile, pic_type }: { file: File; pic_type: string }) => {
    setFileIsUploading(true);

    try {
      const session = await uploadFile({ file: nextFile, pic_type });

      if (session?.status === 200) {
        const { url } = session.data;

        if (!userId) {
          return session;
        }

        try {
          await updatePicture({ userId, payload: { profile_pic_url: url } });
          await queryClient.invalidateQueries({
            queryKey: usersKeys.me(),
          });
          toast.success("آواتار کاربر ویرایش گردید");
        } catch {
          // errors are handled by httpService / upload helpers
        }
      }

      return session;
    } catch (err) {
      console.error("Error uploading file:", err);
      throw err;
    } finally {
      setFileIsUploading(false);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const nextFile = acceptedFiles[0];
      if (!nextFile) {
        return;
      }
      setFile(nextFile);
      setFileUrl(URL.createObjectURL(nextFile));
      void handleUpload({ file: nextFile, pic_type: "USER_PROFILE" });
    },
    [file],
  );
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
  });

  return (
    <>
      <Popup
        trigger={
          <img
            src={src ? src : emptyImage}
            alt="sd"
            className={cn(styles.triggerImage, className)}
            width={width}
            height={height}
          />
        }
        modal
      >
        {/* @ts-expect-error reactjs-popup modal children is a render prop */}
        {(close) => (
          <div className={styles.modal}>
            {src ? (
              <div className={styles.preview}>
                {uploading ? (
                  <div className={styles.progressWrap}>
                    <ProgressBar
                      completed={progress}
                      maxCompleted={100}
                      className={styles.progressWrap}
                      bgColor="#39A78E"
                    />
                  </div>
                ) : (
                  <img src={src} alt="sd" className={styles.fullImage} />
                )}
              </div>
            ) : (
              <div className={styles.preview}>
                <img src={emptyImage} alt="emptyAvatar" className={styles.emptyImage} />
              </div>
            )}

            <div className={styles.actions}>
              <div {...getRootProps()} className={styles.dropzone}>
                <input {...getInputProps()} className="cursor-pointer" />
                <button className="btn-container" type="button">
                  {fileIsUploading ? (
                    <>
                      <div className={styles.loaderWrap}>
                        <Loader />
                      </div>
                      <p> در حال ذخیره سازی ...</p>
                    </>
                  ) : (
                    "ویرایش"
                  )}
                </button>

                {error ? (
                  <div className={styles.error}>خطا:{(error as { message?: string }).message}</div>
                ) : null}
              </div>
              <button onClick={() => close()} className="btn-cancel" type="button">
                انصراف
              </button>
            </div>
          </div>
        )}
      </Popup>
    </>
  );
};

export default ProfileImageContainer;
